import { Button, Grid, Modal, TextField } from "@mui/material";
import { Box, color } from "@mui/system";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import AreaModel from "./models/AreaModel";
import { portSelectorSelector, resetBookmarkInput, setBookmarkInputAreas, setBookmarkInputName, setOpenModal, setBookmarkInput } from "./reducers/PortSelectorReducer";
import PortMultipleSelector from "./PortMultipleSelector";
import Utility from "../../app/utility";
import SavedSearchApi, { DeleteSavedSearchParam, SaveSavedSearchParam } from "../SavedSearch/api/SavedSearchApi";
import { ComponentType } from "../SavedSearch/SavedSearch";
import BaseResponse from "../../app/BaseResponse";
import SavedSearchModel from "../SavedSearch/models/SavedSearchModel";
import { savedSearchSelector, setLoadedSavedSearch } from "../SavedSearch/reducers/SavedSearchReducer";
import { showLoading } from "../LoadingView/reducers/LoadingViewReducer";
import Datatable, { PagingTypeEnum } from "../Datatable/Datatable";
import DatatableColumnModel from "../Datatable/DatatableColumnModel";
import { MouseEvent } from "react";
import AreaBookmarkInputModel from "./models/AreaBookmarkInputModel";

const BookmarkModal: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const portSelectorData = useSelector(portSelectorSelector);
    const savedSearchData = useSelector(savedSearchSelector)

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: "800px",
        bgcolor: 'white',
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
    };

    const saveBookMark = () => {
        if (portSelectorData.bookmarkInput.name.length == 0) {
            Utility.Alert.Error("Bookmark name is required");
            return;
        }

        if (portSelectorData.bookmarkInput.areas.length == 0) {
            Utility.Alert.Error("Bookmark areas is required");
            return;
        }

        dispatch(showLoading(true));

        SavedSearchApi.Save({
            id: portSelectorData.bookmarkInput.id,
            name: portSelectorData.bookmarkInput.name,
            component: ComponentType.AreaBookmark,
            queryJson: JSON.stringify(portSelectorData.bookmarkInput.areas)
        } as SaveSavedSearchParam)
            .then((response: BaseResponse<SavedSearchModel>) => {
                if (response.success) {
                    dispatch(setLoadedSavedSearch(false));
                    dispatch(resetBookmarkInput());

                    Utility.Alert.Success("Save Successful");
                } else {
                    Utility.Alert.Error(response.message);
                }
            })
            .catch((error) => {
                Utility.Alert.Error(error);
            })
            .finally(() => {
                dispatch(showLoading(false));
            });
    }

    const refreshInput = () => {
        dispatch(resetBookmarkInput());
    }

    const columns = [
        {
            Field: "name",
            Title: "Name",
            AllowSort: true,
        } as DatatableColumnModel<SavedSearchModel>,
        {
            Field: "queryJson",
            Title: "Areas",
            AllowSort: true,
            Render: (rowData: SavedSearchModel) => {
                let areas = JSON.parse(rowData.queryJson) as AreaModel[];
                return <>
                    {
                        _.map(areas, (area) => {
                            return <span className={area.positive ? "tag-filter tag-filter-positive" : "tag-filter tag-filter-negative"}>{area.name}</span>
                        })
                    }
                </>
            }
        } as DatatableColumnModel<SavedSearchModel>,
        {
            Field: "",
            Title: "",
            AllowSort: true,
            Render: (rowData: SavedSearchModel) => {
                return <>
                    <Button
                        className="table-action-button"
                        variant="outlined"
                        color="error"
                        onClick={() => {
                            SavedSearchApi.Delete({
                                id: rowData.id
                            } as DeleteSavedSearchParam)
                                .then((response: BaseResponse<string>) => {
                                    if (response.success) {
                                        dispatch(setLoadedSavedSearch(false));
                                    } else {
                                        Utility.Alert.Error(response.message);
                                    }
                                })
                                .catch((error) => {
                                    Utility.Alert.Error(error);
                                })
                                .finally(() => {
                                    dispatch(showLoading(false));
                                });
                        }}
                    >
                        <i className="fa fa-trash" aria-hidden="true"></i>
                    </Button>
                </>
            }
        } as DatatableColumnModel<SavedSearchModel>,
    ];

    return (
        <>
            <Modal
                open={portSelectorData.openBookmarkModel}
                onClose={() => {
                    dispatch(setOpenModal(false));
                }}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={style}>
                    <Grid container>
                        <Grid item xs={4}>
                            <TextField
                                className='cargo-input-component'
                                fullWidth
                                label="Name"
                                value={portSelectorData.bookmarkInput.name ? portSelectorData.bookmarkInput.name : ""}
                                onChange={(event) => {
                                    let value = event.target.value.length == 0 ? "" : event.target.value;
                                    dispatch(setBookmarkInputName(value));
                                }}
                            >
                            </TextField>
                        </Grid>
                        <Grid item xs={1}>
                            <Button
                                className="clear-input-button"
                                tabIndex={-1}
                                color="success"
                                variant="contained"
                                onClick={() => {
                                    refreshInput();
                                }}
                            >
                                <i className="fa fa-refresh" aria-hidden="true"></i>
                            </Button>
                        </Grid>
                        <Grid item xs={5}>
                            <PortMultipleSelector
                                onAreaChange={(area: AreaModel[]) => {
                                    dispatch(setBookmarkInputAreas(area));
                                }}
                                useFreeText={true}
                                areaLabel='Areas'
                                areaValues={portSelectorData.bookmarkInput.areas}
                                className='cargo-input-component'
                                onlyAreaMode={true}
                            >
                            </PortMultipleSelector>
                        </Grid>
                        <Grid item xs={2}>
                            <Button
                                onClick={() => {
                                    saveBookMark();
                                }}
                                variant="outlined"
                            >
                                Save
                            </Button>
                        </Grid>
                    </Grid>

                    <Datatable<SavedSearchModel>
                        useDefaultSearchBar={false}
                        pagingType={PagingTypeEnum.None}
                        data={_.filter(savedSearchData.savedSearches, (savedSearch) => {
                            return savedSearch.component == ComponentType.AreaBookmark;;
                        })}
                        useItemPerPage={false}
                        columns={columns}
                        isDataLoaded={true}
                        useHeaderSort={true}
                        stickyHeader={true}
                        onRowSelectedChange={(selectedRow: SavedSearchModel, selectedIndex: number, event: MouseEvent<HTMLElement>) => {
                            var target = event.target as HTMLElement;
                            if (target.tagName !== "BUTTON") {
                                dispatch(setBookmarkInput({
                                    id: selectedRow.id,
                                    name: selectedRow.name,
                                    areas: JSON.parse(selectedRow.queryJson) as AreaModel[],
                                    isUpdate: true
                                } as AreaBookmarkInputModel));
                            }
                        }}
                    ></Datatable>
                </Box>
            </Modal>
        </>
    )
};

export default BookmarkModal;